/**
 * Standalone Import Rules Editor modal — uses the same field definitions
 * and lookup-backed dropdowns as the import flow.
 */
import { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { LookupFieldSelect } from './LookupFieldSelect';
import {
  getImportConfigForBrand,
  saveImportConfigForBrand,
  type PersistedFieldRule,
  type PersistedExtractionRule,
  type PersistedExclusionRule,
} from '../../services/firebase';
import toast from 'react-hot-toast';

// ── Reuse the same field definitions from ImportFieldRules ────────────────────
const SOURCE_FIELDS = [
  { value: 'title', label: 'Title' },
  { value: 'productName', label: 'Product Name' },
  { value: 'productType', label: 'Product Type' },
  { value: 'description', label: 'Description' },
  { value: 'variant', label: 'Variant' },
  { value: 'movement', label: 'Movement' },
  { value: 'tags', label: 'Tags' },
  { value: 'model', label: 'Model' },
  { value: 'collection', label: 'Collection' },
  { value: 'caseMaterial', label: 'Case Material' },
  { value: 'dialColor', label: 'Dial Color' },
];

const CONDITIONS = [
  { value: 'contains', label: 'contains' },
  { value: 'equals', label: 'equals' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

interface TargetDef {
  value: string; label: string;
  type: 'text' | 'lookup' | 'boolean' | 'multi';
  lookupCollection?: string;
  group: string;
}

const TARGET_FIELDS: TargetDef[] = [
  // Basic Info
  { value: 'collection', label: 'Collection', type: 'text', group: 'Basic Info' },
  { value: 'model', label: 'Model', type: 'text', group: 'Basic Info' },
  { value: 'bezelType', label: 'Bezel Type', type: 'lookup', lookupCollection: 'lookup_bezel_types', group: 'Basic Info' },
  { value: 'bezelMaterial', label: 'Bezel Material', type: 'lookup', lookupCollection: 'lookup_bezel_material', group: 'Basic Info' },
  { value: 'bezelColors', label: 'Bezel Colors', type: 'lookup', lookupCollection: 'lookup_bezel_colors', group: 'Basic Info' },
  { value: 'caseColors', label: 'Case Colors', type: 'lookup', lookupCollection: 'lookup_case_colors', group: 'Basic Info' },
  { value: 'crownType', label: 'Crown Type', type: 'lookup', lookupCollection: 'lookup_crown_types', group: 'Basic Info' },
  // Movement
  { value: 'calibre', label: 'Calibre', type: 'text', group: 'Movement' },
  { value: 'movement', label: 'Base Movement', type: 'text', group: 'Movement' },
  { value: 'movementType', label: 'Movement Type', type: 'lookup', lookupCollection: 'lookup_movement_types', group: 'Movement' },
  { value: 'jewels', label: 'Jewels', type: 'text', group: 'Movement' },
  { value: 'isChronograph', label: 'Chronograph', type: 'boolean', group: 'Movement' },
  { value: 'isGMT', label: 'GMT', type: 'boolean', group: 'Movement' },
  { value: 'chronometer', label: 'Chronometer', type: 'boolean', group: 'Movement' },
  { value: 'manualWinding', label: 'Manual Winding', type: 'boolean', group: 'Movement' },
  { value: 'quickSetDate', label: 'Quick Set Date', type: 'boolean', group: 'Movement' },
  { value: 'isInHouse', label: 'In-House', type: 'boolean', group: 'Movement' },
  { value: 'columnWheel', label: 'Column Wheel', type: 'boolean', group: 'Movement' },
  { value: 'doubleBarrel', label: 'Double Barrel', type: 'boolean', group: 'Movement' },
  { value: 'functions', label: 'Functions (comma-separated)', type: 'multi', group: 'Movement' },
  // Case
  { value: 'caseSize', label: 'Case Size (mm)', type: 'text', group: 'Case' },
  { value: 'caseHeight', label: 'Case Thickness (mm)', type: 'text', group: 'Case' },
  { value: 'lugWidth', label: 'Lug Width (mm)', type: 'text', group: 'Case' },
  { value: 'caseMaterial', label: 'Case Material', type: 'lookup', lookupCollection: 'lookup_case_materials', group: 'Case' },
  // A sealed case that stays sealed as ambient pressure DROPS — the opposite
  // direction from water resistance. A boolean, not a rating: see
  // CaseInfo.isLowPressureResistant in shared-types.
  { value: 'isLowPressureResistant', label: 'Low Pressure Resistant', type: 'boolean', group: 'Case' },
  { value: 'waterRes', label: 'Water Resistance', type: 'lookup', lookupCollection: 'lookup_water_resistance', group: 'Case' },
  { value: 'caseFinish', label: 'Case Finish', type: 'lookup', lookupCollection: 'lookup_case_finishes', group: 'Case' },
  { value: 'caseCoating', label: 'Case Coating', type: 'lookup', lookupCollection: 'lookup_case_coatings', group: 'Case' },
  { value: 'caseBack', label: 'Case Back', type: 'lookup', lookupCollection: 'lookup_caseback_type', group: 'Case' },
  // Dial & Hands
  { value: 'dialColor', label: 'Dial Color', type: 'lookup', lookupCollection: 'lookup_dial_colors', group: 'Dial & Hands' },
  { value: 'dialFinish', label: 'Dial Finish', type: 'lookup', lookupCollection: 'lookup_dial_finish', group: 'Dial & Hands' },
  { value: 'hands', label: 'Hands', type: 'lookup', lookupCollection: 'lookup_dial_hands', group: 'Dial & Hands' },
  { value: 'crystal', label: 'Crystal', type: 'lookup', lookupCollection: 'lookup_crystal_materials', group: 'Dial & Hands' },
  { value: 'lumePresence', label: 'Lume Presence', type: 'lookup', lookupCollection: 'lookup_lume_presence', group: 'Dial & Hands' },
  { value: 'lumeMaterial', label: 'Lume Material', type: 'lookup', lookupCollection: 'lookup_lume_material', group: 'Dial & Hands' },
  { value: 'lumeDayColor', label: 'Lume Day Color', type: 'lookup', lookupCollection: 'lookup_lume_day_color', group: 'Dial & Hands' },
  { value: 'lumeGlowColor', label: 'Lume Glow Color', type: 'lookup', lookupCollection: 'lookup_lume_glow_color', group: 'Dial & Hands' },
  { value: 'subdialTypes', label: 'Subdial Types', type: 'lookup', lookupCollection: 'lookup_dial_subdials', group: 'Dial & Hands' },
  { value: 'chapterRing', label: 'Chapter Ring', type: 'lookup', lookupCollection: 'lookup_chapter_ring_type', group: 'Dial & Hands' },
  { value: 'minuteTrack', label: 'Minute Track', type: 'lookup', lookupCollection: 'lookup_minute_track_type', group: 'Dial & Hands' },
  { value: 'dialScales', label: 'Dial Scales', type: 'lookup', lookupCollection: 'lookup_dial_scales', group: 'Dial & Hands' },
  { value: 'dialFeatures', label: 'Dial Features', type: 'lookup', lookupCollection: 'lookup_dial_features', group: 'Dial & Hands' },
  { value: 'logoType', label: 'Logo Type', type: 'lookup', lookupCollection: 'lookup_dial_logo_type', group: 'Dial & Hands' },
  // Strap / Bracelet
  { value: 'strapMaterial', label: 'Strap Material', type: 'lookup', lookupCollection: 'lookup_strap_materials', group: 'Strap / Bracelet' },
  { value: 'strapColor', label: 'Strap Color', type: 'lookup', lookupCollection: 'lookup_strap_color', group: 'Strap / Bracelet' },
  // Production
  { value: 'isLimited', label: 'Limited Edition', type: 'boolean', group: 'Production' },
  // Two names for one number. `limitedQty` is this importer's original rule
  // field; `limitedEditionCount` is what the enrichment prompt offers and what
  // ExtractedWatch carries since shared-types v1.124.0. BulkImport reads
  // whichever is present, so both stay selectable.
  { value: 'limitedEditionCount', label: 'Limited Edition Size', type: 'text', group: 'Production' },
  { value: 'limitedQty', label: 'Limited Edition Quantity (legacy)', type: 'text', group: 'Production' },
  { value: 'specialEdition', label: 'Special Edition', type: 'boolean', group: 'Production' },
];

const EXTRACTION_SOURCES = [
  { value: 'description', label: 'Description' },
  { value: 'title', label: 'Title' },
  { value: 'variant', label: 'Variant' },
  { value: 'tags', label: 'Tags' },
  { value: 'productName', label: 'Product Name' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  brandId: string;
  brandName: string;
}

function noValue(c: string) { return c === 'is_empty' || c === 'is_not_empty'; }

function TargetValueInput({ rule, onChange }: { rule: PersistedFieldRule; onChange: (v: string) => void }) {
  const def = TARGET_FIELDS.find((f) => f.value === rule.targetField);
  if (def?.type === 'lookup' && def.lookupCollection) {
    return <LookupFieldSelect label="" value={rule.targetValue} lookupCollection={def.lookupCollection} onChange={onChange} placeholder="Select..." />;
  }
  if (def?.type === 'boolean') {
    return (
      <select value={rule.targetValue} onChange={(e) => onChange(e.target.value)} className="text-xs border rounded px-1 py-0.5">
        <option value="">Select...</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  return <input type="text" value={rule.targetValue} onChange={(e) => onChange(e.target.value)} placeholder={def?.type === 'multi' ? 'val1, val2...' : 'value'} className="text-xs border rounded px-1 py-0.5 flex-1 min-w-[80px]" />;
}

export function ImportRulesEditorModal({ isOpen, onClose, brandId, brandName }: Props) {
  const [fieldRules, setFieldRules] = useState<PersistedFieldRule[]>([]);
  const [extractionRules, setExtractionRules] = useState<PersistedExtractionRule[]>([]);
  const [exclusionRules, setExclusionRules] = useState<PersistedExclusionRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    getImportConfigForBrand(brandId)
      .then((c) => { setFieldRules(c.rules); setExtractionRules(c.extractionRules); setExclusionRules(c.exclusionRules); })
      .catch(() => toast.error('Failed to load rules'))
      .finally(() => setLoading(false));
  }, [isOpen, brandId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveImportConfigForBrand(brandId, fieldRules, extractionRules, exclusionRules);
      toast.success('Rules saved');
      onClose();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updateField = (i: number, updates: Partial<PersistedFieldRule>) => {
    setFieldRules((prev) => prev.map((r, j) => j === i ? { ...r, ...updates } : r));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Import Rules — ${brandName}`} size="lg">
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        {loading ? (
          <p className="text-center text-gray-400 py-8">Loading...</p>
        ) : (
          <>
            {/* Field Rules */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700">Field Mapping Rules ({fieldRules.length})</h3>
                <Button variant="secondary" size="sm" onClick={() => setFieldRules([...fieldRules, { sourceField: 'title', condition: 'contains', value: '', targetField: 'movementType', targetValue: '', enabled: true }])}>+ Add</Button>
              </div>
              {fieldRules.length === 0 && <p className="text-xs text-gray-400">No field rules defined</p>}
              <div className="space-y-2">
                {fieldRules.map((rule, i) => (
                  <div key={i} className={`border rounded p-2 space-y-1 ${!rule.enabled ? 'opacity-40' : ''}`}>
                    <div className="flex items-center gap-1 flex-wrap text-xs">
                      <span className="font-medium text-gray-500">IF</span>
                      <select value={rule.sourceField} onChange={(e) => updateField(i, { sourceField: e.target.value })} className="border rounded px-1 py-0.5 text-xs">
                        {SOURCE_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                      </select>
                      <select value={rule.condition} onChange={(e) => updateField(i, { condition: e.target.value })} className="border rounded px-1 py-0.5 text-xs">
                        {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                      {!noValue(rule.condition) && (
                        <input value={rule.value} onChange={(e) => updateField(i, { value: e.target.value })} className="border rounded px-1 py-0.5 text-xs flex-1 min-w-[80px]" placeholder="value" />
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-wrap text-xs">
                      <span className="font-medium text-gray-500">SET</span>
                      <select value={rule.targetField} onChange={(e) => updateField(i, { targetField: e.target.value, targetValue: '' })} className="border rounded px-1 py-0.5 text-xs">
                        {[...new Set(TARGET_FIELDS.map((f) => f.group))].map((g) => (
                          <optgroup key={g} label={g}>
                            {TARGET_FIELDS.filter((f) => f.group === g).map((f) => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <span className="text-gray-400">=</span>
                      <TargetValueInput rule={rule} onChange={(v) => updateField(i, { targetValue: v })} />
                      <label className="flex items-center gap-0.5 ml-1">
                        <input type="checkbox" checked={rule.enabled} onChange={(e) => updateField(i, { enabled: e.target.checked })} className="rounded text-xs" />
                        <span className="text-gray-400">On</span>
                      </label>
                      <button onClick={() => setFieldRules(fieldRules.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 ml-1">✕</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Exclusion Rules */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700">Exclusion Rules ({exclusionRules.length})</h3>
                <Button variant="secondary" size="sm" onClick={() => setExclusionRules([...exclusionRules, { sourceField: 'title', condition: 'contains', value: '', enabled: true }])}>+ Add</Button>
              </div>
              {exclusionRules.length === 0 && <p className="text-xs text-gray-400">No exclusion rules defined — all entries will be imported</p>}
              <div className="space-y-2">
                {exclusionRules.map((rule, i) => (
                  <div key={i} className={`border border-red-200 bg-red-50/30 rounded p-2 ${!rule.enabled ? 'opacity-40' : ''}`}>
                    <div className="flex items-center gap-1 flex-wrap text-xs">
                      <span className="font-medium text-red-600">SKIP IF</span>
                      <select value={rule.sourceField} onChange={(e) => { const r = [...exclusionRules]; r[i] = { ...r[i], sourceField: e.target.value }; setExclusionRules(r); }} className="border rounded px-1 py-0.5 text-xs">
                        {SOURCE_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                      </select>
                      <select value={rule.condition} onChange={(e) => { const r = [...exclusionRules]; r[i] = { ...r[i], condition: e.target.value }; setExclusionRules(r); }} className="border rounded px-1 py-0.5 text-xs">
                        {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                      {!noValue(rule.condition) && (
                        <input value={rule.value} onChange={(e) => { const r = [...exclusionRules]; r[i] = { ...r[i], value: e.target.value }; setExclusionRules(r); }} className="border rounded px-1 py-0.5 text-xs flex-1 min-w-[80px]" placeholder="value" />
                      )}
                      <label className="flex items-center gap-0.5 ml-1">
                        <input type="checkbox" checked={rule.enabled} onChange={(e) => { const r = [...exclusionRules]; r[i] = { ...r[i], enabled: e.target.checked }; setExclusionRules(r); }} className="rounded text-xs" />
                        <span className="text-gray-400">On</span>
                      </label>
                      <button onClick={() => setExclusionRules(exclusionRules.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 ml-1">✕</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Extraction Rules */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700">Calibre Extraction Rules ({extractionRules.length})</h3>
                <Button variant="secondary" size="sm" onClick={() => setExtractionRules([...extractionRules, { sourceField: 'description', pattern: '' }])}>+ Add</Button>
              </div>
              {extractionRules.length === 0 && <p className="text-xs text-gray-400">No extraction rules defined</p>}
              <div className="space-y-2">
                {extractionRules.map((rule, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs border rounded p-2 flex-wrap">
                    <span className="text-gray-500">From</span>
                    <select value={rule.sourceField} onChange={(e) => { const r = [...extractionRules]; r[i] = { ...r[i], sourceField: e.target.value }; setExtractionRules(r); }} className="border rounded px-1 py-0.5 text-xs">
                      {EXTRACTION_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <span className="text-gray-500">match</span>
                    <input value={rule.pattern} onChange={(e) => { const r = [...extractionRules]; r[i] = { ...r[i], pattern: e.target.value }; setExtractionRules(r); }} className="border rounded px-1 py-0.5 flex-1 font-mono text-xs" placeholder="regex pattern (group 1)" />
                    <span className="text-gray-500">→</span>
                    <input value={rule.replaceWith || ''} onChange={(e) => { const r = [...extractionRules]; r[i] = { ...r[i], replaceWith: e.target.value || undefined }; setExtractionRules(r); }} className="border rounded px-1 py-0.5 flex-1 text-xs" placeholder="use as-is (or type replacement)" />
                    <span className="text-gray-500">into</span>
                    <select value={rule.targetField || 'calibre'} onChange={(e) => { const r = [...extractionRules]; r[i] = { ...r[i], targetField: e.target.value as 'calibre' | 'movement' }; setExtractionRules(r); }} className="border rounded px-1 py-0.5 text-xs">
                      <option value="calibre">Calibre</option>
                      <option value="movement">Base Movement</option>
                    </select>
                    <button onClick={() => setExtractionRules(extractionRules.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">✕</button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} loading={saving}>Save Rules</Button>
      </div>
    </Modal>
  );
}
